/**
 * Steady drift for the bamboo benefits row.
 *
 * The row is not a scroller and cannot be dragged: it moves at its own pace and nothing else. The
 * movement is a CSS transform animation, which the browser runs on the compositor, so it stays smooth
 * to the sub-pixel instead of stepping a whole pixel at a time the way a scrolled container does.
 *
 * All this does is: copy the items once so the loop has something to run into, measure the distance
 * from the first item to its copy — that is exactly one lap — and hand the distance and the matching
 * duration to CSS. It then stays out of the way, apart from pausing while the row is off screen.
 *
 * Vanilla: no imports, no framework, no theme runtime.
 */
if (!customElements.get('sustena-bamboo-marquee')) {
  class SustenaBambooMarquee extends HTMLElement {
    #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** @type {HTMLElement | null} */
    #track = null;
    /** @type {ResizeObserver | null} */
    #resizeObserver = null;
    /** @type {IntersectionObserver | null} */
    #visibility = null;
    #lap = 0;

    connectedCallback() {
      this.#track = this.querySelector('[data-marquee-track]');
      if (!this.#track) return;

      this.#duplicate();
      this.#measure();

      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(this);

      this.#visibility = new IntersectionObserver((entries) => {
        const onScreen = entries[entries.length - 1]?.isIntersecting ?? false;
        this.toggleAttribute('paused', !onScreen);
      });
      this.#visibility.observe(this);

      this.#motion.addEventListener('change', this.#onMotionChange);
    }

    disconnectedCallback() {
      this.#resizeObserver?.disconnect();
      this.#visibility?.disconnect();
      this.#motion.removeEventListener('change', this.#onMotionChange);
    }

    #onMotionChange = () => this.#measure();

    /** A second run of the items, hidden from assistive tech, for the loop to roll into. */
    #duplicate() {
      const track = this.#track;
      if (!track || track.dataset.duplicated === 'true') return;

      for (const item of Array.from(track.children)) {
        const copy = /** @type {HTMLElement} */ (item.cloneNode(true));
        copy.setAttribute('aria-hidden', 'true');
        copy.dataset.marqueeCopy = 'true';
        track.appendChild(copy);
      }
      track.dataset.duplicated = 'true';
    }

    /**
     * One lap is the gap between an item and its own copy: shifting by that much puts the copy exactly
     * where the original was, so the seam never shows. Deriving the duration from it keeps the speed
     * the same in pixels per second however many items there are or however wide they get.
     */
    #measure() {
      const track = this.#track;
      if (!track) return;

      const originals = track.querySelectorAll(':scope > :not([data-marquee-copy])');
      const first = originals[0];
      const firstCopy = track.querySelector(':scope > [data-marquee-copy]');
      if (!(first instanceof HTMLElement) || !(firstCopy instanceof HTMLElement)) return;

      const lap = firstCopy.offsetLeft - first.offsetLeft;
      if (lap <= 0) return;

      // A whole-pixel lap keeps the restart from landing on a half pixel and shimmering
      const rounded = Math.round(lap);
      if (rounded === this.#lap) return;
      this.#lap = rounded;

      const speed = Math.max(1, Number(this.dataset.speed) || 34);
      this.style.setProperty('--sustena-marquee-lap', `${rounded}px`);
      this.style.setProperty('--sustena-marquee-duration', `${(rounded / speed).toFixed(2)}s`);
    }
  }

  customElements.define('sustena-bamboo-marquee', SustenaBambooMarquee);
}
